import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Calculator, ArrowRight, Sparkles } from 'lucide-react';

const fmt = (n) => (n >= 1_000_000
  ? `$${(n / 1_000_000).toFixed(2)}M`
  : `$${Math.round(n).toLocaleString()}`);

/**
 * Interactive ROI calculator.
 *  - Hours saved = reps × hours/week × 50 × hourly
 *  - Revenue lift = leads/mo × 12 × close lift × ACV
 *  - Payback = (savings + revenue_lift) / 49 / 30  (months / days vs Pro)
 */
export const ROICalculator = ({ onBookDemo }) => {
  const [reps, setReps] = useState(10);
  const [hoursPerWeek, setHoursPerWeek] = useState(8);
  const [hourlyRate, setHourlyRate] = useState(85);
  const [leadsPerMo, setLeadsPerMo] = useState(120);
  const [acv, setAcv] = useState(8000);
  const [closeLift, setCloseLift] = useState(0.18); // 18%

  const { savings, lift, total, payback } = useMemo(() => {
    const s = reps * hoursPerWeek * 50 * hourlyRate;
    const l = leadsPerMo * 12 * closeLift * acv;
    const t = s + l;
    const proAnnual = 49 * 12;
    const pb = t > 0 ? (proAnnual / t) * 365 : 365;
    return { savings: s, lift: l, total: t, payback: Math.max(0.3, pb) };
  }, [reps, hoursPerWeek, hourlyRate, leadsPerMo, acv, closeLift]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-6">
      <div className="card-base p-6 lg:p-8 relative overflow-hidden">
        <div className="absolute -right-12 -top-16 h-56 w-56 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-16 h-44 w-44 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
        <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-[#39FF14]" />
              <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// roi calculator</span>
            </div>
            <h2 className="display text-2xl md:text-3xl font-semibold mt-2 leading-tight">
              Project your <span className="gradient-text">first-year return</span>.
            </h2>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              Drag the sliders. We compute the conservative-case savings and revenue lift across your team. Numbers update live.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <Slider label="Reps using RevCore" value={reps} setValue={setReps} min={1} max={200} suffix=" reps" />
              <Slider label="Hours saved / rep / week" value={hoursPerWeek} setValue={setHoursPerWeek} min={1} max={30} suffix=" hrs" />
              <Slider label="Fully-loaded hourly cost" value={hourlyRate} setValue={setHourlyRate} min={20} max={300} step={5} prefix="$" />
              <Slider label="New leads / month" value={leadsPerMo} setValue={setLeadsPerMo} min={0} max={2000} step={10} />
              <Slider label="Average contract value" value={acv} setValue={setAcv} min={500} max={250000} step={500} prefix="$" />
              <Slider label="Close-rate lift from agents" value={Math.round(closeLift * 100)} setValue={(v) => setCloseLift(v / 100)} min={0} max={60} suffix="%" />
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col gap-3">
            <div className="hairline rounded-2xl p-5 bg-[hsl(225,18%,8%)]">
              <div className="text-[10px] uppercase tracking-widest text-white/45">Total first-year impact</div>
              <motion.div
                key={total}
                initial={{ scale: 0.96, opacity: 0.6 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 220, damping: 22 }}
                className="display text-5xl font-semibold mt-2 gradient-text"
              >
                {fmt(total)}
              </motion.div>
              <div className="text-xs text-white/55 mt-1">conservative baseline</div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <Stat label="Hours saved" value={fmt(savings)} accent="#39FF14" />
                <Stat label="Revenue lift" value={fmt(lift)} accent="#FF6B1A" />
              </div>
              <div className="mt-4 hairline rounded-xl p-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/45">Pro payback</div>
                  <div className="display text-xl mt-0.5">{payback < 1 ? `${(payback * 24).toFixed(1)} hours` : `${payback.toFixed(1)} days`}</div>
                </div>
                <TrendingUp className="h-6 w-6 text-[#39FF14]" />
              </div>
              <button
                onClick={onBookDemo}
                data-testid="roi-book-demo"
                className="btn-primary inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium w-full mt-4"
              >
                <Sparkles className="h-4 w-4" /> Show me the playbook — book a demo <ArrowRight className="h-4 w-4" />
              </button>
              <div className="text-[10px] text-white/40 mt-3 text-center">Methodology: 50 working weeks/yr · fully loaded cost · close-rate lift on new pipeline only.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

function Slider({ label, value, setValue, min, max, step = 1, prefix = '', suffix = '' }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between text-xs text-white/55 mb-2">
        <span>{label}</span>
        <span className="mono text-white/85">{prefix}{value.toLocaleString()}{suffix}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="roi-slider w-full"
      />
    </label>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="hairline rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/45">{label}</div>
      <div className="display text-xl mt-0.5" style={{ color: accent }}>{value}</div>
    </div>
  );
}
